// @herefor/print — printable plan views and print stylesheets.
//
// A printout is an export, not a screenshot. It is assembled from an already-
// authorised, display-ready read model and then deliberately minimised for the
// semi-public place it will live: a kitchen wall, folder or noticeboard. Nothing in
// this package fetches data, derives coverage or decides who may export it.

export type PrintMode = "discreet" | "full";
export type PrintPeriodDays = 7 | 14;
export type PrintState = "needsSomeone" | "covered" | "pleaseRecheck" | "done" | "cancelled";

export interface PrintResponsibleSource {
  readonly fullName: string;
  readonly discreetName: string;
  /** The canonical proxy-provenance sentence, or null for a self-made commitment. */
  readonly fullProvenance: string | null;
  readonly discreetProvenance: string | null;
}

export interface PrintPracticalUpdateSource {
  readonly text: string;
  readonly provenance: string;
}

export interface PrintNeedSource {
  readonly id: string;
  readonly fullLabel: string;
  readonly discreetLabel: string;
  /** The need's own complete help window, already formatted in the circle's zone. */
  readonly windowLabel: string;
  readonly localDate: string;
  readonly state: PrintState;
  readonly stateLabel: string;
  readonly coverageCountLabel: string | null;
  readonly responsible: readonly PrintResponsibleSource[];
  /** Only rows explicitly opted into print; rendered in full mode only. */
  readonly practicalUpdates: readonly PrintPracticalUpdateSource[];
}

export interface PrintEventSource {
  readonly id: string;
  readonly privateTitle: string;
  /** Separately authored/validated safe title; never derived from `privateTitle`. */
  readonly discreetTitle: string;
  readonly localDate: string;
  readonly windowLabel: string | null;
  readonly locationLabel: string | null;
  readonly state: "done" | "cancelled" | null;
  readonly stateLabel: string | null;
  readonly needs: readonly PrintNeedSource[];
}

export interface PrintScheduleSource {
  readonly planName: string;
  /**
   * The supported person's chosen name, for the heading of THEIR OWN sheet
   * (PRT-002, plan §25.12 "use Ann's chosen name").
   *
   * Null when the circle has not recorded one, and the sheet is headed neutrally
   * rather than with a guess. Separate from `planName` on purpose: the plan title
   * is the family's ("Here for Ann"), and a sheet addressed to somebody should not
   * be titled with the sentence other people use about them.
   */
  readonly supportedPersonName: string | null;
  readonly scheduleStartLocal: string;
  readonly generatedAtLabel: string;
  readonly freshnessWarning: string;
  /**
   * Said out loud when the supported person's name has not been confirmed (bead
   * hf-86o; D-091, bead `hf-e10-hardening-pilot-f4o.5`).
   *
   * Null when the name is settled, so nothing is printed. Paper is the case this
   * exists for: the screen can carry a warning beside a placeholder name and a
   * printout on a kitchen wall cannot, so the notice has to travel onto the page
   * itself. Unlike the contact line it is NOT gated on mode — a discreet printout is
   * still a printout, and a guessed spelling is wrong in both of them.
   */
  readonly supportedPersonNotice: string | null;
  /** Saved default for the §20.4 line on the supported person's own sheet. */
  readonly contactPersonName: string | null;
  readonly contactText: string | null;
  readonly emptyDayLabel: string;
  readonly events: readonly PrintEventSource[];
  /** Needs with no event still belong on the day they cover. */
  readonly standaloneNeeds: readonly PrintNeedSource[];
}

export interface PrintScheduleOptions {
  /** Privacy by default: an omitted mode is always discreet. */
  readonly mode?: PrintMode;
  /** The fridge-door default is one week. */
  readonly periodDays?: PrintPeriodDays;
  /** Ignored unless mode is `full`; contact details require two deliberate choices. */
  readonly includeContact?: boolean;
}

export interface PrintResponsibleView {
  readonly displayName: string;
  readonly provenance: string | null;
}

export interface PrintNeedView {
  readonly id: string;
  readonly label: string;
  readonly windowLabel: string;
  readonly state: PrintState;
  readonly stateLabel: string;
  readonly coverageCountLabel: string | null;
  readonly responsible: readonly PrintResponsibleView[];
  readonly practicalUpdates: readonly PrintPracticalUpdateSource[];
}

export interface PrintEventView {
  readonly id: string;
  readonly title: string;
  readonly windowLabel: string | null;
  readonly locationLabel: string | null;
  readonly state: "done" | "cancelled" | null;
  readonly stateLabel: string | null;
  readonly needs: readonly PrintNeedView[];
}

export interface PrintDayView {
  readonly localDate: string;
  readonly dateLabel: string;
  readonly events: readonly PrintEventView[];
  readonly standaloneNeeds: readonly PrintNeedView[];
  readonly emptyLabel: string | null;
}

export interface PrintScheduleView {
  readonly planName: string;
  readonly mode: PrintMode;
  readonly periodDays: PrintPeriodDays;
  readonly generatedAtLabel: string;
  readonly freshnessWarning: string;
  /** Printed in both modes when the name is unconfirmed; see the source field. */
  readonly supportedPersonNotice: string | null;
  readonly contactText: string | null;
  readonly days: readonly PrintDayView[];
}

/**
 * Minimise an authorised plan for paper without re-deriving any domain fact.
 *
 * The default is intentionally encoded here, below the UI. A future view that forgets
 * to pass options still receives the safe rendering: separate safe event titles,
 * generic need labels, first names, no locations and no contact line.
 */
export function buildPrintSchedule(
  source: PrintScheduleSource,
  options: PrintScheduleOptions = {},
): PrintScheduleView {
  const mode = options.mode ?? "discreet";
  const periodDays = options.periodDays ?? 7;
  const includeContact = mode === "full" && options.includeContact === true;

  return {
    planName: source.planName,
    mode,
    periodDays,
    generatedAtLabel: source.generatedAtLabel,
    freshnessWarning: source.freshnessWarning,
    supportedPersonNotice: source.supportedPersonNotice,
    contactText: includeContact ? source.contactText : null,
    days: Array.from({ length: periodDays }, (_, offset) => {
      const localDate = addLocalDays(source.scheduleStartLocal, offset);
      const events = source.events
        .filter((event) => event.localDate === localDate)
        .map((event) => eventView(event, mode));
      const standaloneNeeds = source.standaloneNeeds
        .filter((need) => need.localDate === localDate)
        .map((need) => needView(need, mode));

      return {
        localDate,
        dateLabel: formatLocalDate(localDate),
        events,
        standaloneNeeds,
        emptyLabel:
          events.length === 0 && standaloneNeeds.length === 0 ? source.emptyDayLabel : null,
      };
    }),
  };
}

function eventView(source: PrintEventSource, mode: PrintMode): PrintEventView {
  return {
    id: source.id,
    title: mode === "discreet" ? source.discreetTitle : source.privateTitle,
    windowLabel: source.windowLabel,
    locationLabel: mode === "full" ? source.locationLabel : null,
    state: source.state,
    stateLabel: source.stateLabel,
    needs: source.needs.map((need) => needView(need, mode)),
  };
}

function needView(source: PrintNeedSource, mode: PrintMode): PrintNeedView {
  return {
    id: source.id,
    label: mode === "discreet" ? source.discreetLabel : source.fullLabel,
    windowLabel: source.windowLabel,
    state: source.state,
    stateLabel: source.stateLabel,
    coverageCountLabel: source.coverageCountLabel,
    responsible: source.responsible.map((person) => ({
      displayName: mode === "discreet" ? person.discreetName : person.fullName,
      provenance: mode === "discreet" ? person.discreetProvenance : person.fullProvenance,
    })),
    practicalUpdates: mode === "full" ? source.practicalUpdates : [],
  };
}

/* ------------------------------------------------- the supported person's sheet --- */

/**
 * THE SUPPORTED PERSON'S OWN SHEET (PRT-002, PRT-004; plan §20.3, §20.4, §24.10).
 *
 * A DIFFERENT DOCUMENT, NOT A THIRD MODE. `buildPrintSchedule` answers the family's
 * question — who is covering what, and where are the gaps. This one answers hers:
 * who is coming to my house, when do they arrive, where am I going. The two overlap
 * in the underlying facts and in nothing else, which is why this is a separate
 * builder rather than another value of `PrintMode`. A mode implies the same paper
 * with a setting changed; this paper leaves out the entire coverage vocabulary and
 * adds the location the family's discreet mode deliberately withholds.
 *
 * It reads the SAME `PrintScheduleSource`. That is the privacy property worth having:
 * the minimisation lives in one place, below the UI, so a future view cannot assemble
 * a supported-person page out of family fields by forgetting to pass an option.
 */
export interface SupportedPersonScheduleOptions {
  /** The fridge-door default is one week, exactly as for the family sheet. */
  readonly periodDays?: PrintPeriodDays;
  /**
   * The one person to check with (§20.4: "Please check with Mary if plans change").
   *
   * An explicit per-printout override of the saved choice on `PrintScheduleSource`.
   * Omitted uses that saved choice; blank deliberately prints the unnamed sentence
   * for this sheet instead of silently falling back to the stored name.
   */
  readonly contactPersonName?: string | null;
}

export interface SupportedPersonEntryView {
  readonly id: string;
  /**
   * `appointment` is something she is going to; `help` is somebody coming to her.
   * The distinction is the sheet's whole organising idea, so it is data rather than
   * a styling accident.
   */
  readonly kind: "appointment" | "help";
  readonly title: string;
  readonly windowLabel: string | null;
  /** Present on appointments only — she needs to know where she is going. */
  readonly locationLabel: string | null;
  /** First names of the people expected. Empty on an appointment. */
  readonly people: readonly string[];
  /**
   * True while the arrangement is awaiting reconfirmation after a material change.
   *
   * Said out loud rather than silently dropped or silently promised. Paper cannot
   * update itself (§20.6: never present a known stale copy as current), and an
   * arrangement somebody has not re-agreed is exactly the thing that should not read
   * as settled. It marks the ENTRY, not the person: the read model carries the
   * need's state, not each commitment's, so per-person certainty is not available
   * here and is not guessed.
   */
  readonly isBeingChecked: boolean;
}

export interface SupportedPersonDayView {
  readonly localDate: string;
  readonly dateLabel: string;
  readonly entries: readonly SupportedPersonEntryView[];
  readonly emptyLabel: string | null;
}

export interface SupportedPersonScheduleView {
  /** Null when the circle has recorded no name; the view then heads the page neutrally. */
  readonly supportedPersonName: string | null;
  readonly periodDays: PrintPeriodDays;
  readonly generatedAtLabel: string;
  readonly freshnessWarning: string;
  readonly supportedPersonNotice: string | null;
  readonly contactPersonName: string | null;
  readonly days: readonly SupportedPersonDayView[];
}

/**
 * Minimise an authorised plan down to what the supported person needs from paper.
 *
 * PRT-004 IS ENFORCED BY OMISSION, NOT BY FILTERING. The fields this function never
 * reads are the guarantee: `coverageCountLabel` (family workload), `stateLabel`,
 * `practicalUpdates` (organiser notes and their audit provenance),
 * `fullProvenance`/`discreetProvenance` (audit history), `privateTitle` (the event's
 * private wording, which has a separately authored safe twin) and `contactText`
 * (how to reach *her*, which is of no use on her own sheet). Availability and
 * unaccepted requests never reach this function at all: availability is not in the
 * source, and a direct request contributes no `responsible` entry until it is
 * accepted, so an unanswered ask is structurally invisible here.
 *
 * What it does filter is only what would be untrue on paper: cancelled events and
 * needs, and needs nobody has agreed to — an open need is the family's business to
 * solve, not a promise to print in her kitchen.
 */
export function buildSupportedPersonSchedule(
  source: PrintScheduleSource,
  options: SupportedPersonScheduleOptions = {},
): SupportedPersonScheduleView {
  const periodDays = options.periodDays ?? 7;
  const contactPersonName = trimmedOrNull(
    options.contactPersonName === undefined ? source.contactPersonName : options.contactPersonName,
  );

  return {
    supportedPersonName: trimmedOrNull(source.supportedPersonName),
    periodDays,
    generatedAtLabel: source.generatedAtLabel,
    freshnessWarning: source.freshnessWarning,
    supportedPersonNotice: source.supportedPersonNotice,
    contactPersonName,
    days: Array.from({ length: periodDays }, (_, offset) => {
      const localDate = addLocalDays(source.scheduleStartLocal, offset);
      const entries = [
        // An event brings its own needs with it, exactly as on the family sheet, so a
        // help window that runs either side of midnight stays with the appointment it
        // belongs to instead of appearing on a day with no context.
        ...source.events
          .filter((event) => event.localDate === localDate && event.state !== "cancelled")
          .flatMap((event): readonly SupportedPersonEntryView[] => [
            appointmentEntry(event),
            ...event.needs.filter(isExpectedOnPaper).map(helpEntry),
          ]),
        ...source.standaloneNeeds
          .filter((need) => need.localDate === localDate)
          .filter(isExpectedOnPaper)
          .map(helpEntry),
      ];

      return {
        localDate,
        dateLabel: formatLocalDate(localDate),
        entries,
        emptyLabel: entries.length === 0 ? source.emptyDayLabel : null,
      };
    }),
  };
}

/** Somebody has agreed to it and it has not been called off. Nothing else prints. */
function isExpectedOnPaper(need: PrintNeedSource): boolean {
  return need.state !== "cancelled" && need.responsible.length > 0;
}

function appointmentEntry(source: PrintEventSource): SupportedPersonEntryView {
  return {
    id: source.id,
    kind: "appointment",
    // The authored safe title, never `privateTitle`. Her own paper still lives in a
    // room visitors walk through, and the safe title is the wording somebody chose
    // for precisely that (PRT-004).
    title: source.discreetTitle,
    windowLabel: source.windowLabel,
    // Included where the family's discreet sheet withholds it: she is the one
    // travelling there (§20.4 "simple location/appointment wording").
    locationLabel: source.locationLabel,
    people: [],
    isBeingChecked: false,
  };
}

/**
 * WHY THE NEED'S OWN WORDING, WHEN THE EVENT'S IS REPLACED BY ITS SAFE TWIN.
 *
 * The asymmetry is the data model's, not this function's. An event carries two
 * separately authored titles — private, and a safe one written for exactly this kind
 * of paper — so using the private one here would be overruling a choice somebody
 * already made. A need carries one label, and it is the description of the help
 * itself: "Drive there and home", "Be present for the delivery window". That sentence
 * IS §20.4's "who is visiting/collecting/calling". Replacing it with the family
 * sheet's generic stand-in would print that a person is arriving at 09:15 for an
 * unstated reason — less use to her, and no more private.
 */
function helpEntry(source: PrintNeedSource): SupportedPersonEntryView {
  return {
    id: source.id,
    kind: "help",
    title: source.fullLabel,
    windowLabel: source.windowLabel,
    locationLabel: null,
    people: source.responsible.map((person) => person.discreetName),
    isBeingChecked: source.state === "pleaseRecheck",
  };
}

function trimmedOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === "" ? null : trimmed;
}

function addLocalDays(localDate: string, count: number): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (parts === null) return localDate;

  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const instant = new Date(Date.UTC(year, month - 1, day + count, 12));
  return [
    String(instant.getUTCFullYear()).padStart(4, "0"),
    String(instant.getUTCMonth() + 1).padStart(2, "0"),
    String(instant.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function formatLocalDate(localDate: string): string {
  const at = new Date(`${localDate}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return localDate;

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(at);
}

/**
 * Project-native paper layout. Screen preview uses the design-system custom
 * properties; print media resolves to black ink on white paper and never relies on
 * colour to communicate a state.
 */
export const printStylesheet = String.raw`
  .print-route {
    display: grid;
    gap: var(--herefor-space-5);
  }

  .print-controls {
    display: grid;
    gap: var(--herefor-space-4);
    padding: var(--herefor-space-4);
    background: var(--herefor-color-surface-subtle);
    border: 1px solid var(--herefor-color-border-strong);
    border-radius: var(--herefor-radius-md);
  }

  .print-controls fieldset {
    display: grid;
    gap: var(--herefor-space-2);
    margin: 0;
    padding: 0;
    border: 0;
  }

  .print-controls legend {
    margin-block-end: var(--herefor-space-2);
    font-weight: var(--herefor-font-weight-semibold);
  }

  .print-controls label {
    min-block-size: 2.75rem;
    display: flex;
    align-items: flex-start;
    gap: var(--herefor-space-2);
  }

  .print-controls input {
    inline-size: 1.25rem;
    block-size: 1.25rem;
    margin-block-start: 0.15rem;
    accent-color: var(--herefor-color-accent);
  }

  .print-controls__hint {
    display: block;
    color: var(--herefor-color-text-muted);
    font-size: var(--herefor-font-size-sm);
  }

  .print-controls__action {
    min-block-size: 2.75rem;
    inline-size: fit-content;
    padding-inline: var(--herefor-space-5);
    background: var(--herefor-color-accent);
    border: 0;
    border-radius: var(--herefor-radius-md);
    color: var(--herefor-color-text-on-accent);
    font: inherit;
    font-weight: var(--herefor-font-weight-semibold);
  }

  .print-schedule {
    inline-size: min(100%, 210mm);
    margin-inline: auto;
    padding: clamp(var(--herefor-space-4), 4vw, var(--herefor-space-6));
    background: var(--herefor-color-surface);
    border: 1px solid var(--herefor-color-border-strong);
    color: var(--herefor-color-text-primary);
    box-shadow: var(--herefor-elevation-raised);
  }

  .print-schedule__header {
    padding-block-end: var(--herefor-space-4);
    border-block-end: 2px solid var(--herefor-color-border-strong);
  }

  .print-schedule__header h1,
  .print-day h2,
  .print-item h3,
  .print-need h4,
  .print-schedule p {
    margin-block: 0;
  }

  .print-schedule__period {
    margin-block-start: var(--herefor-space-2);
    color: var(--herefor-color-text-muted);
    font-size: var(--herefor-font-size-lg);
  }

  .print-schedule__contact {
    margin-block-start: var(--herefor-space-3);
    padding: var(--herefor-space-3);
    border: 1px solid var(--herefor-color-border-strong);
  }

  /* The unconfirmed-spelling notice (bead hf-86o). Bordered like the contact block
     so it reads as a boxed remark rather than as part of the schedule, and printed
     in black in the print rules below — a warning that greys out on a mono printer
     is a warning nobody sees. */
  .print-schedule__notice {
    margin-block-start: var(--herefor-space-3);
    padding: var(--herefor-space-3);
    border: 1px dashed var(--herefor-color-border-strong);
    font-size: var(--herefor-font-size-lg);
  }

  .print-schedule__days,
  .print-day__items,
  .print-item__needs,
  .print-need__helpers {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .print-schedule__days {
    display: grid;
    gap: var(--herefor-space-4);
    margin-block-start: var(--herefor-space-4);
  }

  .print-day {
    break-inside: avoid;
    padding-block-start: var(--herefor-space-3);
    border-block-start: 1px solid var(--herefor-color-border-strong);
  }

  .print-day__items,
  .print-item__needs {
    display: grid;
    gap: var(--herefor-space-3);
    margin-block-start: var(--herefor-space-3);
  }

  .print-item,
  .print-need {
    break-inside: avoid;
  }

  .print-item__facts,
  .print-need__facts,
  .print-need__helpers {
    display: flex;
    flex-wrap: wrap;
    gap: var(--herefor-space-2) var(--herefor-space-3);
    margin-block-start: var(--herefor-space-1);
  }

  .print-state {
    display: inline-block;
    padding: 0.1em 0.45em;
    border: 1px solid currentColor;
    border-radius: var(--herefor-radius-sm);
    font-weight: var(--herefor-font-weight-semibold);
  }

  .print-need__helpers li {
    break-inside: avoid;
  }

  .print-schedule__freshness {
    margin-block-start: var(--herefor-space-5);
    padding-block-start: var(--herefor-space-3);
    border-block-start: 1px solid var(--herefor-color-border-strong);
    color: var(--herefor-color-text-muted);
    font-size: var(--herefor-font-size-sm);
  }

  /* ── The supported person's own sheet (PRT-002, PRT-005; §20.4, §24.10) ───────────
     Large print is the whole point of this document, so the sizes are stated in
     absolute units for paper rather than inherited from the screen scale. The
     accessibility floor is 14–16 pt body; this sheet takes the top of that range,
     because the family sheet already occupies the bottom of it and a page nobody has
     to lean towards is the one that gets used. Everything else — monochrome, borders
     in black, avoided page breaks — is inherited from the .print-schedule rules above,
     which is why the class is applied alongside that one rather than instead of it. */
  .print-schedule--supported {
    font-size: var(--herefor-font-size-lg);
    line-height: 1.6;
  }

  .print-schedule--supported .print-day h2 {
    font-size: 1.6em;
  }

  .print-entry {
    break-inside: avoid;
    margin-block-start: var(--herefor-space-3);
  }

  .print-entry--help {
    padding-inline-start: var(--herefor-space-4);
    border-inline-start: 2px solid var(--herefor-color-border-strong);
  }

  .print-entry__facts,
  .print-entry__people {
    display: flex;
    flex-wrap: wrap;
    gap: var(--herefor-space-2) var(--herefor-space-3);
    margin: var(--herefor-space-1) 0 0;
    padding: 0;
    list-style: none;
  }

  .print-schedule__check-with {
    margin-block-start: var(--herefor-space-4);
    padding: var(--herefor-space-3);
    border: 1px solid var(--herefor-color-border-strong);
    font-weight: var(--herefor-font-weight-semibold);
  }

  @media (min-width: 52rem) {
    .print-controls {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: start;
    }

    .print-controls__action,
    .print-controls__contact-option {
      grid-column: 1 / -1;
    }
  }

  @page {
    size: A4 portrait;
    margin: 12mm 12mm 24mm;
  }

  @media print {
    :root {
      color-scheme: light;
    }

    html,
    body,
    .app-shell,
    .shell-body,
    .shell-main,
    .shell-panel,
    .print-route,
    .print-schedule {
      min-inline-size: 0;
      min-block-size: 0;
      inline-size: auto;
      max-inline-size: none;
      margin: 0;
      padding: 0;
      background: #fff;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      color: #000;
    }

    .shell-header,
    .shell-body > nav,
    .print-route__intro,
    .print-controls,
    .connectivity-banner {
      display: none !important;
    }

    .print-schedule {
      font-size: 14pt;
      line-height: 1.45;
      print-color-adjust: economy;
      -webkit-print-color-adjust: economy;
    }

    /* 16 pt body and a heading nobody has to lean towards — the top of the §24.10
       range, not its floor. Two classes on purpose, so this wins over the 14 pt rule
       above by specificity rather than by staying below it in the file. */
    .print-schedule.print-schedule--supported {
      font-size: 16pt;
      line-height: 1.6;
    }

    .print-schedule--supported .print-day h2 {
      font-size: 24pt;
    }

    .print-schedule__period,
    .print-schedule__notice,
    .print-schedule__freshness {
      color: #000;
    }

    .print-schedule__header,
    .print-day,
    .print-schedule__notice,
    .print-schedule__contact,
    .print-schedule__check-with,
    .print-schedule__freshness,
    .print-state {
      border-color: #000;
    }

    .print-item,
    .print-need,
    .print-entry,
    .print-day,
    .print-need__helpers li {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .print-schedule__freshness {
      position: fixed;
      inset: auto 0 -18mm;
      min-block-size: 14mm;
      padding-block-start: 2mm;
      background: #fff;
      font-size: 9pt;
    }

    /* The dated footer repeats on every page (§24.10), so on the large-print sheet it
       has to be readable there too — 9 pt furniture on a 16 pt page is a timestamp
       that only the person who printed it can check. It stays inside the 24 mm bottom
       margin declared by @page. */
    .print-schedule--supported .print-schedule__freshness {
      inset: auto 0 -20mm;
      min-block-size: 20mm;
      font-size: 14pt;
    }

    a {
      color: #000;
      text-decoration: none;
    }
  }
`;
